import { ReactNode } from 'react';
import {
    InputBase,
    InputBaseProps,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    SvgIconProps,
    Typography,
} from '@mui/material';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';

/**
 * Building blocks that imitate the real invoicing tool, which is Hurdlr's UI rather than ours:
 * underlined fields with a small label above, and serif section headings. The theme's Input draws
 * a box, so these sit on InputBase, and the serif is a system one because the app loads none.
 */

export const HURDLR_SERIF = 'Georgia, "Times New Roman", serif';

const UNDERLINE = {
    typography: 'body2',
    borderBottom: 1,
    borderColor: 'cement.300',
    py: 0.75,
    '&.Mui-focused': { borderColor: 'primary.main' },
    // The real fields have no number spinners.
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button': { WebkitAppearance: 'none', m: 0 },
};

function SmallChevron(props: Readonly<SvgIconProps>) {
    return <ChevronDownIcon {...props} fontSize="small" />;
}

export function SectionHeading({ children }: Readonly<{ children: ReactNode }>) {
    return (
        <Typography variant="h4" sx={{ fontFamily: HURDLR_SERIF, fontWeight: 400, mb: 2 }}>
            {children}
        </Typography>
    );
}

interface FieldProps {
    label: string;
    children: ReactNode;
    flex?: number;
}

export function Field({ label, children, flex }: Readonly<FieldProps>) {
    return (
        <Stack sx={{ flex, minWidth: 0 }}>
            <Typography variant="caption">{label}</Typography>
            {children}
        </Stack>
    );
}

export function UnderlineInput({ sx, ...props }: Readonly<InputBaseProps>) {
    return <InputBase fullWidth {...props} sx={{ ...UNDERLINE, ...sx }} />;
}

interface UnderlineSelectProps<T extends string> {
    label: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (value: T) => void;
    placeholder?: string;
}

export function UnderlineSelect<T extends string>({
    label,
    value,
    options,
    onChange,
    placeholder,
}: Readonly<UnderlineSelectProps<T>>) {
    const handleChange = (e: SelectChangeEvent<T>) => onChange(e.target.value as T);

    return (
        <Select
            value={value}
            onChange={handleChange}
            displayEmpty
            IconComponent={SmallChevron}
            input={<InputBase sx={UNDERLINE} />}
            SelectDisplayProps={{ 'aria-label': label }}
            renderValue={(selected) =>
                options.find((o) => o.value === selected)?.label ?? (
                    <Typography variant="body2" color="text.disabled" component="span">
                        {placeholder}
                    </Typography>
                )
            }>
            {options.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                    {o.label}
                </MenuItem>
            ))}
        </Select>
    );
}
